import { SitesService } from './../../../shared/services/sites.service';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { Site } from '@classes/site';
import { AddressesService } from '@services/addresses.service';
import { Address } from '@classes/address';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

interface CivilityItem { id: number; name: string; }

@Component({
  selector: 'app-address-edit',
  templateUrl: './address-edit.component.html',
  styleUrls: ['./address-edit.component.scss']
})
export class AddressEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() address: Address;
  sites: Site[] = [];

  selectableSites: Site[] = [];
  isSender = false;
  isReceiver = false;
  stringMaxLength = 50;

  tagPattern = "^[a-z0-9]{0,50}$";
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private addressSrv: AddressesService,
    private sitesSrv: SitesService,
    private cd: ChangeDetectorRef
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  initSub(): void {
    this.isLoading = true;
    this.sitesSrv.getList().subscribe(sitesFromSrv => {
      this.sites = sitesFromSrv;
      this.isLoading = false;

      this.initForm();
    });
  }

  get tags() {
    return this.editForm.get('tags');
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.address) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const addressCopy: Address = JSON.parse(JSON.stringify(this.address));
    addressCopy.name = this.editForm.value.name;
    addressCopy.street = this.editForm.value.street;
    addressCopy.streetNumber = this.editForm.value.streetNumber;
    addressCopy.locality = this.editForm.value.locality;
    addressCopy.postalCode = this.editForm.value.postalCode;
    addressCopy.isSender = this.editForm.value.isSender;
    addressCopy.isReceiver = this.editForm.value.isReceiver;
    addressCopy.sites = this.editForm.value.sites;
    addressCopy.phone = this.editForm.value.phone;
    addressCopy.tags = this.editForm.value.tags;

    this.addressSrv.put(addressCopy).subscribe(() => this.isLoading = false);
  }

  private create(): void {
    this.address = new Address(
      this.editForm.value.name,
      this.editForm.value.street,
      this.editForm.value.streetNumber,
      this.editForm.value.locality,
      this.editForm.value.postalCode,
      this.editForm.value.isSender,
      this.editForm.value.isReceiver,
      this.editForm.value.sites,
      this.editForm.value.phone,
      this.editForm.value.tags
    );

    this.addressSrv.post(this.address)
      .subscribe(
        () => this.isLoading = false,
        () => this.address = null
      );
  }

  private initForm(): void {
    let name = "";
    let street = "";
    let streetNumber = "";
    let locality = "";
    let postalCode = "";
    let isSender = false;
    let isReceiver = false;
    let sites: Site[] = [];
    let phone = '';
    let tags = [];

    if (this.address) {
      name = this.address.name;
      street = this.address.street;
      streetNumber = this.address.streetNumber;
      locality = this.address.locality;
      postalCode = this.address.postalCode;
      isSender = this.address.isSender;
      isReceiver = this.address.isReceiver;
      sites = this.address.sites;
      phone = this.address.phone;
      tags = this.address.tags;
    }

    this.editForm = this.fb.group({
      name: [name, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      street: [street, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      streetNumber: [streetNumber, [Validators.maxLength(this.stringMaxLength)]],
      locality: [locality, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      postalCode: [postalCode, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      isSender: [isSender],
      isReceiver: [isReceiver],
      sites: this.fb.array([]),
      phone: [phone, [Validators.maxLength(this.stringMaxLength)]],
      tags: new UntypedFormControl(tags)
    });

    if (sites && sites.length) {
      sites.map(s => this.onAddSite(s));
    } else {
      this.onAddSite();
    }
  }

  onAddSite(site?: Site): void {
    if (!site) {
      this.generateSelectableSites();
    }
    const control = this.fb.control(site || null);
    control.setValidators(Validators.required);
    this.getArray('sites').push(control);


    this.cd.detectChanges();
  }

  onRemoveSite(index: number): void {
    this.getArray('sites').removeAt(index);

    this.generateSelectableSites();
  }

  addTag(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add our tag
    if ((value || '').trim()) {
      if (value.length <= 50) {
        this.tags.setValue([... this.tags.value, value.trim()]);
        this.tags.updateValueAndValidity();
      }

    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  removeTag(tag: string): void {
    const index = this.editForm.value.tags.indexOf(tag);

    if (index >= 0) {
      this.tags.value.splice(index, 1);
      this.tags.updateValueAndValidity();
    }
  }

  generateSelectableSites(): void {
    this.selectableSites = [...this.sites].filter(
      (worksite) =>
        !this.alreadyUsedSites.map((item) => item.id).includes(worksite.id)
    );
  }

  get sitesFormArray(): FormArray {
    return this.editForm.get('sites') as FormArray;
  }

  get alreadyUsedSites(): Site[] {
    return this.sitesFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }
}
